import {
  Alert,
  AlertDialog,
  Badge,
  Button,
  Card,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  toast,
} from '@1k5-tech-es/grado-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, History, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useBlocker, useSearchParams } from 'react-router';

import { api, ApiError } from '../../api/client';
import type { EditableField, FieldSpec, MonthlyGrid, MonthlyRow, Period, PeriodSummary } from '../../api/types';
import { ErrorBlock, LoadingBlock, StatusPill, STATUS_CLASSES } from '../../components/ui';
import { formatNumber, formatPercent, monthName, MONTH_NAMES, periodLabel } from '../../lib/format';
import { DERIVED_COLUMNS, deriveMetrics, evaluateKpi, salesStatus, type Values } from '../../lib/kpi';
import { describeSource, INPUT_COLUMNS, INPUT_GROUPS } from './columns';
import { HistoryDialog } from './HistoryDialog';
import { RadarPanel } from './RadarPanel';

type Drafts = Record<number, Partial<Record<EditableField, string>>>;

function rowValues(row: MonthlyRow): Values {
  return { ...row.performance, ...row.targets };
}

function toInput(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

/** Read-only display of a saved value: thousands separators, no trailing zeros. */
function formatInput(value: number | null, decimals: number): string {
  if (value === null) return '';
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits: decimals, useGrouping: 'always' }).format(value);
}

function parseInput(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '');
  if (trimmed === '') return null;
  const normalized = trimmed.replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : Number.NaN;
}

function validate(value: number | null, spec: FieldSpec | undefined): string | null {
  if (value === null || !spec) return null;
  if (Number.isNaN(value)) return 'Número no válido';
  if (spec.integer && !Number.isInteger(value)) return 'Debe ser un número entero';
  if (spec.min !== undefined && value < spec.min) return `Mínimo ${spec.min}`;
  if (value > spec.max) return `Máximo ${formatNumber(spec.max)}`;
  return null;
}

function defaultPeriod(periods: PeriodSummary[] | undefined): Period {
  const latest = periods?.find((p) => p.performanceRows > 0);
  if (latest) return { year: latest.year, month: latest.month };
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

export function MonthlyPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const periodsQuery = useQuery({
    queryKey: ['periods'],
    queryFn: () => api.get<{ periods: PeriodSummary[] }>('/api/monthly/periods').then((r) => r.periods),
  });

  const fallback = defaultPeriod(periodsQuery.data);
  const period: Period = {
    year: Number(params.get('anio')) || fallback.year,
    month: Number(params.get('mes')) || fallback.month,
  };
  const ready = params.has('anio') || periodsQuery.isSuccess || periodsQuery.isError;

  const gridQuery = useQuery({
    queryKey: ['monthly', period.year, period.month],
    queryFn: () => api.get<MonthlyGrid>(`/api/monthly/${period.year}/${period.month}`),
    enabled: ready,
  });

  const [drafts, setDrafts] = useState<Drafts>({});
  const [teamFilter, setTeamFilter] = useState('');
  const [search, setSearch] = useState('');
  const [historyFor, setHistoryFor] = useState<MonthlyRow | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [pendingPeriod, setPendingPeriod] = useState<Period | null>(null);

  const grid = gridQuery.data;
  const rows = useMemo(() => grid?.rows ?? [], [grid]);
  const rowsById = useMemo(() => new Map(rows.map((r) => [r.advisor.id, r])), [rows]);

  // Effective values (saved values with drafts applied) and validation errors
  const { effective, errors, dirtyCount } = useMemo(() => {
    const effectiveValues = new Map<number, Values>();
    const errorMap = new Map<string, string>();
    let dirty = 0;
    for (const row of rows) {
      const values = rowValues(row);
      const draft = drafts[row.advisor.id] ?? {};
      for (const [field, raw] of Object.entries(draft) as [EditableField, string][]) {
        const parsed = parseInput(raw);
        const error = validate(parsed, grid?.fieldSpecs[field]);
        if (error) errorMap.set(`${row.advisor.id}:${field}`, error);
        if (parsed !== values[field]) dirty += 1;
        values[field] = Number.isNaN(parsed) ? null : parsed;
      }
      effectiveValues.set(row.advisor.id, values);
    }
    return { effective: effectiveValues, errors: errorMap, dirtyCount: dirty };
  }, [rows, drafts, grid]);

  const hasUnsaved = dirtyCount > 0;
  const blocker = useBlocker(({ currentLocation, nextLocation }) => hasUnsaved && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!hasUnsaved) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  const save = useMutation({
    mutationFn: () => {
      const payload = Object.entries(drafts).flatMap(([id, draft]) => {
        const row = rowsById.get(Number(id));
        if (!row) return [];
        const saved = rowValues(row);
        const values: Partial<Record<EditableField, number | null>> = {};
        for (const [field, raw] of Object.entries(draft) as [EditableField, string][]) {
          const parsed = parseInput(raw);
          if (parsed !== saved[field]) values[field] = parsed;
        }
        return Object.keys(values).length ? [{ advisorId: Number(id), values }] : [];
      });
      return api.put<MonthlyGrid>(`/api/monthly/${period.year}/${period.month}`, { rows: payload });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['monthly', period.year, period.month], data);
      void queryClient.invalidateQueries({ queryKey: ['periods'] });
      void queryClient.invalidateQueries({ queryKey: ['quarters'] });
      setDrafts({});
      toast.success(`Se han guardado ${data.changedFields ?? 0} valores.`);
    },
    onError: (error) => setSaveError(error instanceof ApiError ? error.message : 'No se han podido guardar los cambios.'),
  });

  function goToPeriod(next: Period) {
    setDrafts({});
    setSaveError(null);
    setPendingPeriod(null);
    setParams({ anio: String(next.year), mes: String(next.month) });
  }

  function changePeriod(next: Period) {
    if (hasUnsaved) setPendingPeriod(next);
    else goToPeriod(next);
  }

  function shift(delta: number) {
    const index = period.year * 12 + period.month - 1 + delta;
    changePeriod({ year: Math.floor(index / 12), month: (index % 12) + 1 });
  }

  function setDraft(advisorId: number, field: EditableField, raw: string) {
    setSaveError(null);
    setDrafts((current) => ({ ...current, [advisorId]: { ...current[advisorId], [field]: raw } }));
  }

  const teamCodes = useMemo(
    () => [...new Set(rows.map((r) => r.advisor.teamCode ?? 'Sin equipo'))].sort(),
    [rows],
  );
  const visibleRows = rows.filter((r) => {
    if (teamFilter && (r.advisor.teamCode ?? 'Sin equipo') !== teamFilter) return false;
    return !search || r.advisor.fullName.toLowerCase().includes(search.toLowerCase());
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

  return (
    <>
      <PageHeader
        title="Datos mensuales"
        subtitle="Objetivos y actividad de cada asesor por mes. Las columnas calculadas y sus colores se actualizan al escribir; los informes trimestrales se generan a partir de estos datos."
        actions={
          <>
            {hasUnsaved && <Badge tone="warning">{dirtyCount} cambios sin guardar</Badge>}
            <Button variant="secondary" disabled={!hasUnsaved || save.isPending} onClick={() => setDrafts({})}>
              Descartar
            </Button>
            <Button disabled={!hasUnsaved || errors.size > 0} loading={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </>
        }
      />

      <Card className="mb-4 flex flex-wrap items-end gap-4 p-4">
        {/* Two selects share one label, so a plain k5-field instead of Field (which gives one id). */}
        <div className="k5-field" role="group" aria-labelledby="month-label">
          <span id="month-label" className="k5-field__label">
            Mes
          </span>
          <div className="flex items-center gap-1">
            <IconButton variant="secondary" icon={ChevronLeft} label="Mes anterior" onClick={() => shift(-1)} />
            <Select
              aria-label="Mes"
              className="w-36"
              value={period.month}
              onChange={(e) => changePeriod({ ...period, month: Number(e.target.value) })}
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1}>
                  {name}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Año"
              className="w-24"
              value={period.year}
              onChange={(e) => changePeriod({ ...period, year: Number(e.target.value) })}
            >
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </Select>
            <IconButton variant="secondary" icon={ChevronRight} label="Mes siguiente" onClick={() => shift(1)} />
          </div>
        </div>
        <Field label="Equipo">
          <Select className="w-40" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
            <option value="">Todos</option>
            {teamCodes.map((code) => (
              <option key={code}>{code}</option>
            ))}
          </Select>
        </Field>
        <Field label="Buscar asesor" className="min-w-48 flex-1">
          <Input
            placeholder="Nombre…"
            prefix={<Search className="size-4" aria-hidden />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
        <Legend />
      </Card>

      <RadarPanel period={period} />

      {saveError && (
        <Alert tone="danger" title="No se han guardado los cambios" className="mb-4" onDismiss={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {gridQuery.isPending ? (
        <LoadingBlock />
      ) : gridQuery.isError ? (
        <ErrorBlock error={gridQuery.error} />
      ) : (
        <Card>
          <div className="border-b border-border px-4 py-3 text-sm text-muted-foreground">
            <strong className="text-foreground">{periodLabel(period)}</strong> · {visibleRows.length} asesores
          </div>
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-20 bg-muted text-xs text-muted-foreground">
                <tr>
                  <th rowSpan={2} className="sticky left-0 z-30 min-w-52 border-b border-border bg-muted px-4 py-2 text-left font-semibold">
                    Asesor
                  </th>
                  {INPUT_GROUPS.map((g) => (
                    <th key={g.label} colSpan={g.columns.length} className="border-b border-l border-border px-2 py-1.5 text-center font-semibold tracking-wide uppercase">
                      {g.label}
                    </th>
                  ))}
                  <th colSpan={DERIVED_COLUMNS.length} className="border-b border-l border-border bg-primary-subtle px-2 py-1.5 text-center font-semibold tracking-wide text-primary-text uppercase">
                    Indicadores (calculados)
                  </th>
                  <th rowSpan={2} className="border-b border-l border-border px-2" />
                </tr>
                <tr>
                  {INPUT_COLUMNS.map((c) => (
                    <th key={c.field} title={c.hint} className="border-b border-l border-border px-2 py-2 text-right font-medium whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                  {DERIVED_COLUMNS.map((c) => (
                    <th key={c.key} title={c.label} className="border-b border-l border-border bg-primary-subtle px-2 py-2 text-center font-medium whitespace-nowrap">
                      {c.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const id = row.advisor.id;
                  const values = effective.get(id)!;
                  const derived = deriveMetrics(values);
                  const draft = drafts[id] ?? {};
                  const saved = rowValues(row);
                  return (
                    <tr key={id} className="group">
                      <td className="sticky left-0 z-10 border-b border-border bg-card px-4 py-1.5 group-hover:bg-muted">
                        <div className="font-medium">{row.advisor.fullName}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.advisor.teamCode ?? 'Sin equipo'}
                          {!row.advisor.isActive && ' · inactivo'}
                        </div>
                      </td>
                      {INPUT_COLUMNS.map((column) => {
                        const key = `${id}:${column.field}`;
                        const raw =
                          draft[column.field] ??
                          (focusedKey === key ? toInput(saved[column.field]) : formatInput(saved[column.field], column.decimals));
                        const isDirty = draft[column.field] !== undefined && parseInput(draft[column.field]!) !== saved[column.field];
                        const error = errors.get(key);
                        const source = describeSource(row.sources[column.field]);
                        const statusClass =
                          column.field === 'salesAmount'
                            ? salesStatus(values.salesAmount, values.minimumExpected, values.target)
                            : null;
                        return (
                          <td
                            key={column.field}
                            className={`relative border-b border-l border-border p-0 ${statusClass ? `${STATUS_CLASSES[statusClass]} font-semibold` : 'group-hover:bg-muted'}`}
                          >
                            <input
                              aria-label={`${column.label} · ${row.advisor.fullName}`}
                              aria-invalid={error ? true : undefined}
                              title={error ?? source?.label}
                              inputMode="decimal"
                              value={raw}
                              onFocus={() => setFocusedKey(key)}
                              onBlur={() => setFocusedKey(null)}
                              onChange={(e) => setDraft(id, column.field, e.target.value)}
                              className={`h-9 w-24 px-2 text-right tabular-nums outline-none focus:ring-2 focus:ring-ring focus:ring-inset ${
                                error
                                  ? 'bg-destructive-subtle text-destructive-text ring-2 ring-destructive ring-inset'
                                  : isDirty
                                    ? 'bg-warning-subtle text-foreground'
                                    : 'bg-transparent focus:bg-card'
                              }`}
                            />
                            {source && source.tone !== 'manual' && !isDirty && (
                              <span
                                aria-hidden
                                className={`pointer-events-none absolute top-1 right-1 size-1.5 rounded-full ${source.tone === 'integration' ? 'bg-info' : 'bg-muted-foreground/40'}`}
                              />
                            )}
                          </td>
                        );
                      })}
                      {DERIVED_COLUMNS.map((c) => (
                        <td key={c.key} className="border-b border-l border-border px-2 py-1 text-center group-hover:bg-muted">
                          <StatusPill status={evaluateKpi(grid!.rules, c.key, derived[c.key])}>
                            {c.format === 'percent' ? formatPercent(derived[c.key]) : formatNumber(derived[c.key], 1)}
                          </StatusPill>
                        </td>
                      ))}
                      <td className="border-b border-l border-border px-1 text-center group-hover:bg-muted">
                        <IconButton icon={History} size="sm" label="Historial de cambios" onClick={() => setHistoryFor(row)} />
                      </td>
                    </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={INPUT_COLUMNS.length + DERIVED_COLUMNS.length + 2} className="px-4 py-10 text-center text-muted-foreground">
                      No hay asesores que coincidan con el filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {historyFor && <HistoryDialog row={historyFor} period={period} onClose={() => setHistoryFor(null)} />}
      <p className="mt-3 text-xs text-muted-foreground">
        Los decimales se escriben con coma. Dejar una celda vacía borra el valor. Mes mostrado: {monthName(period.month)}.
      </p>

      <DiscardDialog
        open={pendingPeriod !== null}
        text="Tienes cambios sin guardar en este mes. Si cambias de mes, se descartan."
        confirmLabel="Cambiar de mes"
        onConfirm={() => pendingPeriod && goToPeriod(pendingPeriod)}
        onCancel={() => setPendingPeriod(null)}
      />
      <DiscardDialog
        open={blocker.state === 'blocked'}
        text="Tienes cambios sin guardar. Si sales de esta página, se descartan."
        confirmLabel="Salir sin guardar"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </>
  );
}

function DiscardDialog({
  open,
  text,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  text: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => !next && onCancel()}
      title="Cambios sin guardar"
      description={text}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Seguir editando
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-muted-foreground/40" /> Importado del Excel
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-info" /> Integración
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-2 rounded-sm bg-warning-subtle ring-1 ring-warning-border" /> Sin guardar
      </span>
    </div>
  );
}
